import { AppIcon, visuallyHiddenSx } from '@backrs_ui/common-ui';

import {
  dayBadge,
  formatLongDate,
  formatWeekday,
} from '@backrs_ui/common-ui/src/utils/dateTime.util';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  DetailHeader,
  detailHeaderChipSx,
  detailHeaderCompletePillSx,
} from '@backrs_ui/grow-ui/src/components/DetailHeader/DetailHeader';
import { PlanDay } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

import { Box, Button, Chip, SxProps, Theme, Tooltip } from '@mui/material';

const STRINGS = appStrings.modules.satActBooster.dailyPractice.header;

const TOGGLE_BLOCKED_ID = 'booster-day-complete-blocked-reason';

type DayHeaderProps = Readonly<{
  day: PlanDay;
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onToggleComplete: () => void;
  toggleDisabled: boolean;
  /** When set, the toggle is blocked and this is the reason shown on hover or focus. */
  toggleBlockedReason?: string;
}>;

/**
 * Day-detail header: a date badge with the full date, weekday, and Week-N chip, plus prev/next/Today
 * navigation and the completion toggle. Selecting a different date swaps the day, not the tab title.
 */
export const DayHeader = ({
  day,
  canPrev,
  canNext,
  onPrev,
  onNext,
  onToday,
  onToggleComplete,
  toggleDisabled,
  toggleBlockedReason,
}: DayHeaderProps) => {
  const completed = day.completed;
  // An empty reason would disable the toggle while leaving nothing to explain it.
  const blockedReason = toggleBlockedReason || undefined;

  return (
    <DetailHeader
      id="booster-day-header"
      // Matches the other cards on the daily-practice page
      borderColor="Beige/950"
      badge={dayBadge(day.date)}
      title={formatLongDate(day.date)}
      titleAdornment={
        <Chip
          size="sm"
          variant="badge"
          label={STRINGS.weekChip(day.week)}
          sx={detailHeaderChipSx}
        />
      }
      subtitle={formatWeekday(day.date)}
      actions={
        <>
          <Box sx={navGroupSx}>
            <Button
              id="booster-day-prev-btn"
              sx={navArrowSx}
              disabled={!canPrev}
              onClick={onPrev}
              aria-label={STRINGS.prevDayAria}
            >
              <AppIcon iconName="ArrowLeft" diameter="20px" color="currentColor" />
            </Button>
            <Button id="booster-day-today-btn" sx={navTodaySx} onClick={onToday}>
              {STRINGS.today}
            </Button>
            <Button
              id="booster-day-next-btn"
              sx={navArrowSx}
              disabled={!canNext}
              onClick={onNext}
              aria-label={STRINGS.nextDayAria}
            >
              <AppIcon iconName="ArrowRight" diameter="20px" color="currentColor" />
            </Button>
          </Box>

          {/* A disabled button takes neither pointer events nor focus, so the span carries the
          hover target and the tab stop. An empty title renders nothing. */}
          <Tooltip title={blockedReason ?? ''} describeChild>
            <Box component="span" sx={toggleWrapSx} tabIndex={blockedReason ? 0 : undefined}>
              <Button
                id="booster-day-complete-toggle"
                hierarchy="Secondary color"
                size="md"
                icon="Leading"
                disabled={toggleDisabled || blockedReason !== undefined}
                onClick={onToggleComplete}
                aria-label={STRINGS.toggleAria}
                aria-describedby={blockedReason ? TOGGLE_BLOCKED_ID : undefined}
                aria-pressed={completed}
                startIcon={
                  <AppIcon
                    iconName={completed ? 'CheckCircle' : 'ClockFastForward'}
                    diameter="20px"
                    color="currentColor"
                  />
                }
                sx={completed ? detailHeaderCompletePillSx : undefined}
              >
                {completed ? STRINGS.complete : STRINGS.incomplete}
              </Button>
              {blockedReason && (
                <Box component="span" id={TOGGLE_BLOCKED_ID} sx={visuallyHiddenSx}>
                  {blockedReason}
                </Box>
              )}
            </Box>
          </Tooltip>
        </>
      }
    />
  );
};

const toggleWrapSx: SxProps<Theme> = {
  display: 'inline-flex',
};

const navGroupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'stretch',
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  borderRadius: backrs.radius['radius-md'],
  overflow: 'hidden',
  boxShadow: backrs.shadows['shadow-xs'],
});

const navArrowSx: SxProps<Theme> = ({ backrs }) => ({
  minWidth: 0,
  minHeight: backrs.spacing['spacing-5xl'],
  paddingInline: backrs.spacing['spacing-lg'],
  paddingBlock: backrs.spacing['spacing-md'],
  color: backrs.colors['Colors/Foreground/fg-secondary'],
  borderRadius: 0,
  borderRight: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  '&:last-of-type': { borderRight: 'none' },
  '&.Mui-disabled': { color: backrs.colors['Colors/Text/text-disabled'] },
});

const navTodaySx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-sm-semibold'],
  minWidth: 0,
  minHeight: backrs.spacing['spacing-5xl'],
  paddingInline: backrs.spacing['spacing-xl'],
  paddingBlock: backrs.spacing['spacing-md'],
  color: backrs.colors['Colors/Text/text-secondary'],
  borderRadius: 0,
  borderRight: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  textTransform: 'none',
});
